import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { clsx } from 'clsx';

type DropdownItem<T> = {
  id: T;
  label: string;
  value?: string;
};

type Props<T extends string | number> = {
  items: Array<DropdownItem<T>>;
  selectedId: T | null;
  onSelect: (id: T) => void;
  placeholder?: string;
  triggerClassName?: string;
  contentClassName?: string;
  itemClassName?: string;
  activeItemClassName?: string;
  renderTrigger?: (selectedItem: DropdownItem<T> | undefined, isOpen: boolean) => ReactNode;
  renderItem?: (item: DropdownItem<T>, isActive: boolean) => ReactNode;
  icon?: ReactNode;
  activeIcon?: ReactNode;
  openIcon?: ReactNode;
  closedIcon?: ReactNode;
  sideOffset?: number;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  alignOffset?: number;
};

const Dropdown = <T extends string | number>({
  items,
  selectedId,
  onSelect,
  placeholder,
  triggerClassName,
  contentClassName,
  itemClassName,
  activeItemClassName,
  renderTrigger,
  renderItem,
  icon,
  openIcon,
  closedIcon,
  side = 'bottom',
  align = 'center',
  alignOffset = 0,
  sideOffset = 0,
}: Props<T>) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const selectedItem = items.find(item => item.id === selectedId);

  if (!items.length) {
    return null;
  }

  const triggerIcon = isOpen
    ? openIcon || icon || <ChevronUp size={16} />
    : closedIcon || icon || <ChevronDown size={16} />;
  const triggerClasses = clsx(triggerClassName, isOpen && 'rounded-b-none');

  const defaultTrigger = (
    <button className={triggerClasses}>
      <span className="truncate">{selectedItem?.label || placeholder}</span>

      <span className="ml-auto flex items-center shrink-0">{triggerIcon}</span>
    </button>
  );

  const defaultItem = (item: DropdownItem<T>, isActive: boolean) => (
    <div className={clsx(itemClassName, isActive && activeItemClassName)}>
      <span>{item.label}</span>
    </div>
  );

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger asChild>
        {renderTrigger ? renderTrigger(selectedItem, isOpen) : defaultTrigger}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={contentClassName}
          side={side}
          align={align}
          alignOffset={alignOffset}
          sideOffset={sideOffset}
          style={{ width: 'var(--radix-dropdown-menu-trigger-width)' }}
        >
          {items.map(item => {
            const isActive = selectedId === item.id;

            return (
              <DropdownMenu.Item
                key={item.id}
                className="outline-none cursor-pointer"
                onSelect={() => onSelect(item.id)}
              >
                {renderItem ? renderItem(item, isActive) : defaultItem(item, isActive)}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default Dropdown;
